import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

interface Props
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: string;
  isSubmitting: boolean;
  endMessage: string;
}

export default function SubmitButton({
  children,
  className,
  isSubmitting,
  endMessage,
  ...props
}: Props) {
  const [rect, setRect] = useState<{
    width: number | null;
    height: number | null;
  }>({
    width: null,
    height: null,
  });
  const refButton = useRef<HTMLButtonElement>(null);
  const refHiddenButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!refHiddenButton.current) return;

    if (children === endMessage) {
      // width & height need to be calculated to animate the button,
      // because animating a width of 'auto' is not possible
      // https://css-tricks.com/using-css-transitions-auto-dimensions/
      const { width, height } = refHiddenButton.current.getBoundingClientRect();
      setRect({ width, height });
    }
  }, [children, endMessage]);

  useEffect(() => {
    if (!refButton.current) return;

    const { width, height } = refButton.current.getBoundingClientRect();
    setRect({ width, height });
  }, [refButton]);

  const { width, height } = rect;
  const loadingCircleSize = width && height ? Math.min(width, height) : 'auto';

  return (
    <>
      <button
        ref={refButton}
        className={classNames(
          className,
          'relative text-blue-100 text-13 leading-140 tracking-8 bg-blue-400 hover:bg-blue-800 rounded-full font-bold uppercase px-5 flex justify-center items-center transition-[width, height] duration-500 py-2.5 focus:ring-blue-800 focus:bg-blue-800 focus:ring-1 outline-none',
          {
            'outline-none': isSubmitting,
          },
        )}
        style={{
          width: isSubmitting ? loadingCircleSize : width ? width : 'auto',
          height: isSubmitting ? loadingCircleSize : height ? height : 'auto',
        }}
        {...props}>
        <div
          className={classNames('overflow-hidden flex ', {
            'opacity-100 transition-opacity delay-500': !isSubmitting,
            'opacity-0': isSubmitting,
          })}>
          {children}
        </div>
        <div
          className={classNames(
            'absolute inset-0 transition-opacity rounded-full bg-white border-none',
            {
              'opacity-0': !isSubmitting,
              'opacity-100 scale-105 delay-500': isSubmitting,
            },
          )}>
          {[-0.45, -0.3, -0.15, 0].map((delay, index) => (
            <div
              key={index}
              className={classNames(
                'absolute rounded-full border-4 border-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-loader w-10 h-10',
                {
                  'opacity-0': !isSubmitting,
                  'opacity-100': isSubmitting,
                },
              )}
              style={{
                animationDelay: `${delay}s`,
              }}></div>
          ))}
        </div>
      </button>
      {/* to measure length of end message */}
      <button
        ref={refHiddenButton}
        aria-hidden="true"
        disabled={true}
        className={classNames(
          className,
          'text-blue-100 text-13 leading-140 tracking-8 bg-blue-500 hover:bg-blue-800 rounded-full font-bold uppercase px-5 transition-[width, height] duration-500 py-2.5 invisible absolute -left-1000 top-0',
        )}
        {...props}>
        {endMessage}
      </button>
    </>
  );
}
